import { z } from 'zod'

import {
  heroContentSchema,
  activityCardsContentSchema,
  valuePropsContentSchema,
  featuresContentSchema,
  contactContentSchema,
  partnersContentSchema,
  finalCtaContentSchema,
  footerContentSchema,
} from './landingContent.js'

// ---------------------------------------------------------------------------
// Page type enum
// ---------------------------------------------------------------------------

export const cmsPageTypeEnum = z.enum(['landing', 'blog', 'static', 'health'])
export type CmsPageType = z.infer<typeof cmsPageTypeEnum>

// ---------------------------------------------------------------------------
// Content schemas for new page types
// ---------------------------------------------------------------------------

export const blogPostContentSchema = z.object({
  title: z.string(),
  slug: z.string(),
  author: z.string(),
  excerpt: z.string(),
  body: z.string(),
  heroImageUrl: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().url().startsWith('https://').nullable().default(null),
  ),
  tags: z.array(z.string()).default([]),
  publishedAt: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().datetime().nullable().default(null),
  ),
})
export type BlogPostContent = z.infer<typeof blogPostContentSchema>

export const staticPageContentSchema = z.object({
  title: z.string(),
  slug: z.string(),
  body: z.string(),
  metaDescription: z.string().default(''),
})
export type StaticPageContent = z.infer<typeof staticPageContentSchema>

export const healthPageContentSchema = z.object({
  heading: z.string(),
  body: z.string(),
  statusEndpoint: z.string().default(''),
})
export type HealthPageContent = z.infer<typeof healthPageContentSchema>

/**
 * The landing page content is the full set of sections stored as a single
 * JSON blob (matching the CDN structure). This schema wraps the per-section
 * schemas into one object for storage in a single cms_page_ row.
 */
export const landingPageCmsContentSchema = z.object({
  slug: z.string(),
  industryName: z.string().default(''),
  hero: heroContentSchema.nullable().default(null),
  activityCards: activityCardsContentSchema.nullable().default(null),
  valueProps: valuePropsContentSchema.nullable().default(null),
  features: featuresContentSchema.nullable().default(null),
  contact: contactContentSchema.nullable().default(null),
  partners: partnersContentSchema.nullable().default(null),
  finalCta: finalCtaContentSchema.nullable().default(null),
  footer: footerContentSchema.nullable().default(null),
})
export type LandingPageCmsContent = z.infer<typeof landingPageCmsContentSchema>

// ---------------------------------------------------------------------------
// Field hints -- tell the generic editor how to render specific fields
// ---------------------------------------------------------------------------

export interface FieldHints {
  /** Field paths rendered as markdown editors (supports `*.` for array items) */
  markdown: string[]
  /** Field paths rendered as image upload inputs */
  image: string[]
  /** Field key -> allowed values for enum selects */
  enum: Record<string, string[]>
  /** Field paths rendered as plain text (not markdown) */
  plainText?: string[]
}

// ---------------------------------------------------------------------------
// Page type registry
// ---------------------------------------------------------------------------

export type PageTypeKind = 'singleton' | 'collection'

export interface PageTypeConfig {
  label: string
  kind: PageTypeKind
  contentSchema: z.ZodTypeAny
  fieldHints: FieldHints
  cdnKey: string
  /** For collections: the field in content that holds the URL slug */
  slugField?: string
  /** When true, the admin UI publishes an index manifest after collection mutations. */
  publishIndexOnChange?: boolean
  /** Returns the public-facing URL path for a given slug. Used by admin preview links. */
  getPublicPath?: (slug: string) => string
}

export const pageTypeRegistry: Record<CmsPageType, PageTypeConfig> = {
  landing: {
    label: 'Landing Pages',
    kind: 'collection',
    contentSchema: landingPageCmsContentSchema,
    fieldHints: {
      markdown: [
        'activityCards.subheading',
        'activityCards.cards.*.description',
        'valueProps.subheading',
        'valueProps.items.*.description',
        'features.features.*.body',
        'contact.subheading',
        'partners.subheading',
        'finalCta.subheading',
        'footer.brandDescription',
      ],
      image: [
        'activityCards.cards.*.imageUrl',
        'features.features.*.imageUrl',
        'partners.partners.*.logoUrl',
      ],
      enum: {
        tagColor: ['blue', 'green', 'purple'],
        imagePosition: ['left', 'right'],
      },
      plainText: ['industryName'],
    },
    cdnKey: 'landing/',
    slugField: 'slug',
    publishIndexOnChange: true,
    getPublicPath: (slug) => {
      const s = slug.replace(/^\/+/, '')
      return s === 'default' ? '/' : `/industries/${s}`
    },
  },
  blog: {
    label: 'Blog',
    kind: 'collection',
    contentSchema: blogPostContentSchema,
    fieldHints: {
      markdown: ['body'],
      image: ['heroImageUrl'],
      enum: {},
    },
    cdnKey: 'blog/',
    slugField: 'slug',
    publishIndexOnChange: true,
    getPublicPath: (slug) => `/blog/${slug.replace(/^\/+/, '')}`,
  },
  static: {
    label: 'Static Pages',
    kind: 'collection',
    contentSchema: staticPageContentSchema,
    fieldHints: {
      markdown: ['body'],
      image: [],
      enum: {},
    },
    cdnKey: 'pages/',
    slugField: 'slug',
    publishIndexOnChange: true,
    getPublicPath: (slug) => `/${slug.replace(/^\/+/, '')}`,
  },
  health: {
    label: 'Health Page',
    kind: 'singleton',
    contentSchema: healthPageContentSchema,
    fieldHints: {
      markdown: ['body'],
      image: [],
      enum: {},
    },
    cdnKey: 'health/content.json',
  },
} as const

/**
 * Validates that a page type key is registered.
 */
export function isValidPageType(type: string): type is CmsPageType {
  return type in pageTypeRegistry
}

// ---------------------------------------------------------------------------
// Admin response types
// ---------------------------------------------------------------------------

const cmsPageAdminBase = {
  id: z.string(),
  slug: z.string().nullable(),
  title: z.string().nullable(),
  published: z.boolean(),
  updatedAt: z.coerce.date().nullable(),
  updatedBy: z.string().nullable(),
  updatedByName: z.string().nullable().optional(),
}

export const cmsPageAdminSchema = z.discriminatedUnion('pageType', [
  z.object({
    ...cmsPageAdminBase,
    pageType: z.literal('landing'),
    content: landingPageCmsContentSchema,
  }),
  z.object({
    ...cmsPageAdminBase,
    pageType: z.literal('blog'),
    content: blogPostContentSchema,
  }),
  z.object({
    ...cmsPageAdminBase,
    pageType: z.literal('static'),
    content: staticPageContentSchema,
  }),
  z.object({
    ...cmsPageAdminBase,
    pageType: z.literal('health'),
    content: healthPageContentSchema,
  }),
])

export type CmsPageAdmin = z.infer<typeof cmsPageAdminSchema>

// ---------------------------------------------------------------------------
// CDN index manifests
// ---------------------------------------------------------------------------

export const blogIndexSchema = z.object({
  posts: z.array(
    z.object({
      slug: z.string(),
      title: z.string(),
      author: z.string(),
      excerpt: z.string(),
      heroImageUrl: z.string().nullable(),
      publishedAt: z.string().nullable(),
    }),
  ),
  publishedAt: z.string().datetime().optional(),
})
export type BlogIndex = z.infer<typeof blogIndexSchema>

export const staticPageIndexSchema = z.object({
  pages: z.array(
    z.object({
      slug: z.string(),
      title: z.string(),
    }),
  ),
  publishedAt: z.string().datetime().optional(),
})
export type StaticPageIndex = z.infer<typeof staticPageIndexSchema>
